import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AssignmentSelectionModal,
  AssignmentFetchResult,
  AssignmentOption,
} from './AssignmentSelectionModal';
import { getCompanyDrivers } from '../api/apiService';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onAssign: (employmentIds: number[]) => Promise<void>;
  assignedEmploymentIds: number[];
}

export const DriverAssignmentModal: React.FC<Props> = ({
  visible,
  onDismiss,
  onAssign,
  assignedEmploymentIds,
}) => {
  const { t } = useTranslation('manageVehicles');
  const fetchDrivers = useCallback(
    async (
      page: number,
      pageSize: number,
      search?: string
    ): Promise<AssignmentFetchResult> => {
      const response = await getCompanyDrivers(page, pageSize, search);
      if (!response.ok) {
        throw new Error(
          (response.data as any)?.detail ||
            t('loadDriversError', 'Unable to load drivers')
        );
      }

      const data: any = response.data;
      const results: any[] = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      const items: AssignmentOption[] = results.map((driver: any) => ({
        id: driver.id,
        title: driver.user_full_name || driver.name || driver.user_email,
        description: driver.user_email || driver.email || '',
        icon: 'account',
      }));

      const totalPages =
        typeof data?.total_pages === 'number'
          ? data.total_pages
          : data?.count
          ? Math.max(1, Math.ceil(data.count / pageSize))
          : 1;

      return { items, totalPages };
    },
    [t]
  );

  return (
    <AssignmentSelectionModal
      visible={visible}
      onDismiss={onDismiss}
      onAssign={onAssign}
      assignedIds={assignedEmploymentIds}
      fetchOptions={fetchDrivers}
      title={t('assignDrivers', 'Assign Drivers')}
      searchPlaceholder={t('searchDrivers', 'Search by name or email...')}
      emptySearchText={t(
        'noDriversFound',
        'No drivers found matching your search'
      )}
      emptyListText={t(
        'noAvailableDrivers',
        'No available drivers to assign'
      )}
      selectLabel={t('select', 'Select')}
      selectedLabel={t('selected', 'Selected')}
      selectedCountLabel={(count) =>
        `${count} ${t('selected', 'selected')}`
      }
      assignButtonLabel={t('assign', 'Assign')}
      cancelButtonLabel={t('cancel', 'Cancel')}
      loadErrorMessage={t(
        'loadDriversError',
        'Unable to load drivers'
      )}
      selectionRequiredMessage={t(
        'selectAtLeastOneDriver',
        'Please select at least one driver'
      )}
      listTestId="driver-assignment-list"
    />
  );
};

